import React from "react";
import { cn } from "../../utils/cn";

const Input = React.forwardRef(({
    className,
    type = "text",
    label,
    description,
    error,
    required = false,
    id,
    compact = false,
    ...props
}, ref) => {
    // Generate unique ID if not provided
    const inputId = id || `input-${Math.random()?.toString(36)?.substr(2, 9)}`;

    // Base input classes - smaller and more professional
    const baseInputClasses = cn(
        "flex w-full rounded border border-gray-300 bg-white px-3 py-2",
        "text-xs font-normal text-gray-900",
        "placeholder:text-gray-400",
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400",
        "transition-colors duration-150",
        compact ? "h-8 text-xs" : "h-9 text-xs"
    );

    // Checkbox-specific styles - smaller and cleaner
    if (type === "checkbox") {
        return (
            <div className="flex items-center space-x-2">
                <input
                    type="checkbox"
                    className={cn(
                        "h-4 w-4 rounded border border-gray-300 text-blue-600",
                        "focus:ring-2 focus:ring-blue-500 focus:ring-offset-0",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        className
                    )}
                    ref={ref}
                    id={inputId}
                    {...props}
                />
                {label && (
                    <label
                        htmlFor={inputId}
                        className="text-xs font-medium text-gray-700 select-none"
                    >
                        {label}
                        {required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                )}
            </div>
        );
    }

    // Radio button-specific styles
    if (type === "radio") {
        return (
            <div className="flex items-center space-x-2">
                <input
                    type="radio"
                    className={cn(
                        "h-4 w-4 border border-gray-300 text-blue-600",
                        "focus:ring-2 focus:ring-blue-500 focus:ring-offset-0",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        className
                    )}
                    ref={ref}
                    id={inputId}
                    {...props}
                />
                {label && (
                    <label
                        htmlFor={inputId}
                        className="text-xs font-medium text-gray-700 select-none"
                    >
                        {label}
                        {required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                )}
            </div>
        );
    }

    // Select input
    if (type === "select") {
        return (
            <div className="space-y-1">
                {label && (
                    <label
                        htmlFor={inputId}
                        className={cn(
                            "text-xs font-medium text-gray-700 block",
                            error ? "text-red-600" : "text-gray-700"
                        )}
                    >
                        {label}
                        {required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                )}
                
                <select
                    className={cn(
                        "flex w-full rounded border border-gray-300 bg-white px-3 py-2",
                        "text-xs font-normal text-gray-900",
                        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                        "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400",
                        compact ? "h-8" : "h-9",
                        error && "border-red-500 focus:ring-red-500 focus:border-red-500",
                        className
                    )}
                    ref={ref}
                    id={inputId}
                    {...props}
                />
                
                {description && !error && (
                    <p className="text-xs text-gray-500">
                        {description}
                    </p>
                )}
                
                {error && (
                    <p className="text-xs text-red-600">
                        {error}
                    </p>
                )}
            </div>
        );
    }

    // Textarea
    if (type === "textarea") {
        return (
            <div className="space-y-1">
                {label && (
                    <label
                        htmlFor={inputId}
                        className={cn(
                            "text-xs font-medium block",
                            error ? "text-red-600" : "text-gray-700"
                        )}
                    >
                        {label}
                        {required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                )}
                
                <textarea
                    className={cn(
                        "flex w-full rounded border border-gray-300 bg-white px-3 py-2",
                        "text-xs font-normal text-gray-900",
                        "placeholder:text-gray-400",
                        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                        "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400",
                        "resize-none",
                        error && "border-red-500 focus:ring-red-500 focus:border-red-500",
                        className
                    )}
                    ref={ref}
                    id={inputId}
                    {...props}
                />
                
                {description && !error && (
                    <p className="text-xs text-gray-500">
                        {description}
                    </p>
                )}
                
                {error && (
                    <p className="text-xs text-red-600">
                        {error}
                    </p>
                )}
            </div>
        );
    }

    // For regular inputs
    return (
        <div className="space-y-1">
            {label && (
                <label
                    htmlFor={inputId}
                    className={cn(
                        "text-xs font-medium block",
                        error ? "text-red-600" : "text-gray-700"
                    )}
                >
                    {label}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
            )}

            <input
                type={type}
                className={cn(
                    baseInputClasses,
                    error && "border-red-500 focus:ring-red-500 focus:border-red-500",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />

            {description && !error && (
                <p className="text-xs text-gray-500">
                    {description}
                </p>
            )}

            {error && (
                <p className="text-xs text-red-600">
                    {error}
                </p>
            )}
        </div>
    );
});

Input.displayName = "Input";

export default Input;